import { cn } from '@/lib/utils/cn';

export type StepperStep = {
  id: string;
  label: string;
};

type StepperProps = {
  steps: StepperStep[];
  current: number; // 0-based
  onJump?: (index: number) => void;
};

/**
 * Minimal step indicator for the onboarding wizard.
 * Numbers + labels on desktop, numbers only on mobile.
 */
export function QariStepper({ steps, current, onJump }: StepperProps) {
  return (
    <ol
      aria-label="Onboarding progress"
      className="flex items-center gap-2 sm:gap-3 mb-8 overflow-x-auto"
    >
      {steps.map((step, i) => {
        const state =
          i < current ? 'done' : i === current ? 'active' : 'upcoming';
        const isClickable = onJump && i <= current;
        return (
          <li key={step.id} className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={isClickable ? () => onJump(i) : undefined}
              disabled={!isClickable}
              className={cn(
                'inline-flex items-center gap-2 px-2.5 py-1 rounded-full',
                'text-xs font-medium tracking-tight transition-colors',
                state === 'active' &&
                  'bg-accent text-ink-50',
                state === 'done' &&
                  'bg-ink-100 text-ink-800 dark:bg-ink-800 dark:text-ink-200',
                state === 'upcoming' &&
                  'text-ink-400 dark:text-ink-500',
                isClickable && state !== 'active' && 'hover:bg-ink-200/60 dark:hover:bg-ink-700/60',
                !isClickable && 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px]',
                  state === 'active' && 'bg-ink-50/20',
                  state === 'done' && 'bg-accent text-ink-50',
                  state === 'upcoming' &&
                    'border border-ink-300 dark:border-ink-700',
                )}
              >
                {i + 1}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={cn(
                  'h-px w-4 sm:w-6',
                  i < current
                    ? 'bg-accent/60'
                    : 'bg-ink-200 dark:bg-ink-800',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
